/* =====================================================================
   swt-product-v2.js · SWT Product V2 (sections/swt-product-v2.liquid)  10-19-26
   Part 1 of 2: picker, price, stock, buy, pickup, quantity, add to cart.
   Part 2 (swt-product-v2-media.js) adds gallery, zoom, bars, lower content,
   share, back in stock, and starts everything. Both load with defer, in order.
   - Variant picker: in-stock first (set in Liquid), live "only with other options"
     states, smart switch to the closest in-stock combo, ad links respected.
   - Price / stock / Add to cart / Shop Pay / UPC / URL / pickup refresh.
   - Gallery: swipe + dots at every size, arrows on hover, variant photo jump,
     full-screen viewer. Binkies 3D (shadow root on .swt-pdp__gallery) is left alone.
   - Sticky buy bar (phones/tablets) + jump bar with price and Add to cart (desktop).
   - Add to cart: /cart/add.js then Rebuy Smart Cart (same path as before, so SureBright
     and Rebuy keep working). Optional Klaviyo back-in-stock sign-up.
   ===================================================================== */
(() => {
  'use strict';
  if (window.SwtProductV2) return;

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const desktop = () => window.matchMedia('(min-width: 990px)').matches;

  class SwtProductV2 {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.money = (cents) => {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || undefined, {
            style: 'currency', currency: root.dataset.currency || 'CAD', currencyDisplay: 'narrowSymbol'
          }).format(Number(cents || 0) / 100);
        } catch (e) { return '$' + (Number(cents || 0) / 100).toFixed(2); }
      };
      this.variants = this.readJSON('[data-variants-json]', []);
      this.lowStock = this.readJSON('[data-stock-json]', {});
      this.lowThreshold = Number(root.dataset.lowStock || 0);
      this.groups = [...root.querySelectorAll('[data-opt]')];
      this.select = root.querySelector('[data-variant-select]');
      this.form = root.querySelector('.swt-pp-form');
      this.idInput = root.querySelector('[data-variant-id]');
      this.add = root.querySelector('[data-add]');
      this.current = this.variants.find((v) => String(v.id) === String(this.idInput && this.idInput.value)) || this.variants[0];
      this.initPicker();
      this.initGallery();
      this.initZoom();
      this.initQty();
      this.initForm();
      this.initBars();
      this.initLower();
      this.initShare();
      this.initBis();
      this.refreshStates();
      this.updateSoldout();
    }

    readJSON(sel, fallback) {
      const el = this.root.querySelector(sel);
      if (!el) return fallback;
      try { return JSON.parse(el.textContent); } catch (e) { return fallback; }
    }

    q(sel) { return this.root.querySelector(sel); }
    qa(sel) { return [...this.root.querySelectorAll(sel)]; }

    /* ---------------- Variant picker ---------------- */
    selection() {
      return this.groups.map((g) => {
        const c = g.querySelector('[data-opt-input]:checked');
        return c ? c.value : null;
      });
    }

    find(sel) {
      return this.variants.find((v) => sel.every((val, i) => v.options[i] === val)) || null;
    }

    bestFor(index, value, sel) {
      const pool = this.variants.filter((v) => v.available && v.options[index] === value);
      if (!pool.length) return null;
      let best = null, bestScore = -1;
      pool.forEach((v) => {
        let score = 0;
        sel.forEach((val, i) => { if (i !== index && v.options[i] === val) score += (i < index ? 2 : 1); });
        if (score > bestScore) { best = v; bestScore = score; }
      });
      return best;
    }

    bestOverall(sel) {
      const pool = this.variants.filter((v) => v.available);
      if (!pool.length) return null;
      let best = pool[0], bestScore = -1;
      pool.forEach((v) => {
        let score = 0;
        sel.forEach((val, i) => { if (v.options[i] === val) score += (this.groups.length - i); });
        if (score > bestScore) { best = v; bestScore = score; }
      });
      return best;
    }

    initPicker() {
      this.note = this.q('[data-switch-note]');
      this.groups.forEach((g, index) => {
        g.addEventListener('change', (e) => {
          const input = e.target.closest('[data-opt-input]');
          if (!input) return;
          this.onPick(index, input.value);
        });
      });
      if (this.select) {
        this.select.addEventListener('change', () => {
          const v = this.variants.find((x) => String(x.id) === this.select.value);
          if (v) this.apply(v, { source: 'select' });
        });
      }
      const best = this.q('[data-best]');
      if (best) {
        best.addEventListener('click', () => {
          const v = this.bestOverall(this.current ? this.current.options : this.selection());
          if (v) { this.apply(v, { source: 'best' }); this.announce(`Switched to ${v.title}, in stock.`); }
        });
      }
    }

    onPick(index, value) {
      const sel = this.selection();
      const exact = this.find(sel);
      if (exact && exact.available) { this.hideNote(); this.apply(exact); return; }
      const alt = this.bestFor(index, value, sel);
      if (alt) {
        const changed = [];
        alt.options.forEach((val, i) => {
          if (i !== index && val !== sel[i]) {
            const name = this.groups[i].querySelector('.swt-pp-opt__name');
            changed.push(`${name ? name.textContent.trim() : 'Option'} to ${val}`);
          }
        });
        this.apply(alt);
        if (changed.length) {
          const was = sel.filter((v, i) => i !== index && v !== alt.options[i]).join(' / ');
          this.showNote(`${value} is in stock with other options, so we changed ${changed.join(' and ')}.${was ? ` ${value} in ${was} is sold out.` : ''}`);
        }
        return;
      }
      this.hideNote();
      this.apply(exact || this.variants.find((v) => v.options[index] === value) || this.current);
    }

    showNote(text) {
      if (!this.note) return;
      this.note.textContent = text;
      this.note.hidden = false;
      clearTimeout(this.noteTimer);
      this.noteTimer = setTimeout(() => this.hideNote(), 9000);
    }
    hideNote() { if (this.note) this.note.hidden = true; }

    refreshStates() {
      if (!this.groups.length) return;
      const sel = this.selection();
      this.groups.forEach((g, i) => {
        g.querySelectorAll('[data-opt-input]').forEach((input) => {
          const label = input.closest('.swt-pp-val');
          const soldEverywhere = input.dataset.soldout === 'true';
          const comboOk = this.variants.some((v) => v.available && v.options[i] === input.value && v.options.every((o, j) => j === i || o === sel[j]));
          label.classList.toggle('is-combo-off', !soldEverywhere && !comboOk);
          const note = label.querySelector('[data-val-note]');
          if (note) note.textContent = soldEverywhere ? ', sold out' : (!comboOk ? ', in stock with other options' : '');
        });
        const out = g.querySelector('[data-opt-selected]');
        if (out) out.textContent = sel[i] || '';
      });
    }

    apply(v, opts = {}) {
      if (!v) return;
      this.current = v;
      // radios follow the variant
      this.groups.forEach((g, i) => {
        g.querySelectorAll('[data-opt-input]').forEach((input) => { input.checked = input.value === v.options[i]; });
      });
      if (this.select && opts.source !== 'select') this.select.value = String(v.id);
      this.refreshStates();

      // hidden id: property for the form, attribute for Binkies
      if (this.idInput) {
        this.idInput.value = v.id;
        this.idInput.setAttribute('value', v.id);
        this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // price
      const price = this.q('[data-price]');
      const compare = this.q('[data-compare]');
      const save = this.q('[data-save]');
      const wrap = this.q('[data-price-wrap]');
      const onSale = Number(v.compare_at_price) > Number(v.price);
      if (price) price.textContent = this.money(v.price);
      if (compare) { compare.textContent = onSale ? this.money(v.compare_at_price) : ''; compare.hidden = !onSale; }
      if (save) { save.textContent = onSale ? `Save ${this.money(v.compare_at_price - v.price)}` : ''; save.hidden = !onSale; }
      if (wrap) wrap.classList.toggle('is-sale', onSale);

      // UPC
      const upc = this.q('[data-upc]');
      const upcWrap = this.q('[data-upc-wrap]');
      if (upc) upc.textContent = v.barcode || '';
      if (upcWrap) upcWrap.hidden = !v.barcode;

      this.updateBuy(v);
      this.updateSoldout();
      this.updateBar(v);
      this.goToMedia(v);
      this.refreshPickup(v);

      // URL, so shares and refreshes keep the choice
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (e) { /* ignore */ }

      this.announce(`${v.title}, ${this.money(v.price)}, ${v.available ? 'in stock' : (this.root.dataset.soldText || 'sold out')}`);
      document.dispatchEvent(new CustomEvent('swt:variant:change', { detail: { variant: v, sectionId: this.sectionId } }));
    }

    updateBuy(v) {
      const label = this.q('[data-add-label]');
      const addPrice = this.q('[data-add-price]');
      const pay = this.q('[data-payment]');
      const stock = this.q('[data-stock]');
      const stockText = this.q('[data-stock-text]');
      const err = this.q('[data-error]');
      if (err) err.hidden = true;
      if (this.add) this.add.disabled = !v.available;
      if (label) label.textContent = v.available ? this.root.dataset.addText : this.root.dataset.soldText;
      if (addPrice) { addPrice.textContent = this.money(v.price); addPrice.hidden = !v.available; }
      if (pay) pay.hidden = !v.available;
      if (stock && stockText) {
        const left = this.lowStock[String(v.id)];
        const low = v.available && left && this.lowThreshold > 0 && left <= this.lowThreshold;
        stock.classList.toggle('is-out', !v.available);
        stock.classList.toggle('is-low', !!low);
        stockText.textContent = !v.available ? this.root.dataset.soldText : (low ? `Only ${left} left` : 'In stock');
      }
    }

    updateSoldout() {
      const box = this.q('[data-soldout]');
      if (!box || !this.current) return;
      const v = this.current;
      box.hidden = !!v.available;
      if (v.available) return;
      const best = this.q('[data-best]');
      const text = this.q('[data-soldout-text]');
      const alt = this.bestOverall(v.options);
      if (text) text.textContent = alt ? `${v.title} is sold out. Other options are in stock.` : 'This product is sold out right now.';
      if (best) {
        best.hidden = !alt;
        if (alt) best.textContent = `Show ${alt.title} (in stock)`;
      }
    }

    refreshPickup(v) {
      const wrap = this.q('[data-pickup]');
      if (!wrap) return;
      const base = this.root.dataset.productUrl;
      if (!base) return;
      clearTimeout(this.pickupTimer);
      this.pickupTimer = setTimeout(() => {
        const ctrl = new AbortController();
        if (this.pickupCtrl) this.pickupCtrl.abort();
        this.pickupCtrl = ctrl;
        fetch(`${base}?variant=${v.id}&section_id=swt-pp-pickup`, { signal: ctrl.signal })
          .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const fresh = doc.querySelector('.shopify-section');
            wrap.innerHTML = fresh ? fresh.innerHTML.trim() : '';
          })
          .catch(() => { /* keep the old line */ });
      }, 150);
    }

    announce(text) {
      const live = this.q('[data-live]');
      if (!live) return;
      live.textContent = '';
      setTimeout(() => { live.textContent = text; }, 60);
    }

    /* ---------------- Quantity ---------------- */
    initQty() {
      const input = this.q('[data-qty-input]');
      if (!input) return;
      const clamp = () => { const n = parseInt(input.value, 10); input.value = Number.isFinite(n) && n > 0 ? n : 1; };
      this.q('[data-qty-minus]').addEventListener('click', () => { clamp(); input.value = Math.max(1, Number(input.value) - 1); });
      this.q('[data-qty-plus]').addEventListener('click', () => { clamp(); input.value = Number(input.value) + 1; });
      input.addEventListener('change', clamp);
    }

    /* ---------------- Add to cart ---------------- */
    initForm() {
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        if (!this.add || this.add.disabled) return;
        const err = this.q('[data-error]');
        if (err) err.hidden = true;
        this.add.classList.add('is-loading');
        this.add.setAttribute('aria-busy', 'true');
        fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(this.form) })
          .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
          .then(({ ok, data }) => {
            if (!ok) {
              if (err) { err.textContent = data && (data.description || data.message) ? (data.description || data.message) : 'Could not add to cart. Please try again.'; err.hidden = false; }
              return;
            }
            document.dispatchEvent(new CustomEvent('swt:cart:added', { detail: { item: data } }));
            if (window.Rebuy && window.Rebuy.SmartCart && typeof window.Rebuy.SmartCart.show === 'function') {
              window.Rebuy.SmartCart.show();
            } else {
              window.location.href = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart';
            }
          })
          .catch(() => { this.form.submit(); })
          .finally(() => {
            this.add.classList.remove('is-loading');
            this.add.removeAttribute('aria-busy');
          });
      });
    }
  }

  window.SwtProductV2 = SwtProductV2;
})();